import { Component, OnChanges } from '@angular/core';

import { ParticipantMarketType, ParticipantPriceType, ParticipantType } from '@cds/betting-offer';
import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventOption } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import {
    FixtureParticipantPriceMap,
    HorseRaceSortingDirection,
    RacingParticipantWithEventOption,
    RacingParticipantWithParticipantOption,
    V3FixtureParticipantPriceMap,
    WinGameFixture,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';
import { ParticipantPickType } from 'packages/sports/common/betslip/core/picks/pick-models';

import { OptionType } from '../../../../dropdown/models';
import { HorsesFixtureMarketHelper } from '../../../../horse-racing-shared/services/horses-fixture-market-helper.service';
import { HorsesFixtureParticipantHelper } from '../../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureSortingHelper } from '../../../../horse-racing-shared/services/horses-fixture-sorting-helper.service';
import { GreyhoundTrackingService } from '../../../common/greyhound-tracking.service';
import { ParticipantsService } from '../../../common/participant-service';
import { HorsesRaceService } from '../../../services/horses-race.service';
import { RacingService } from '../../../services/racing-service';
import { BaseRaceWinMarketService } from '../../base/base-race-win-market.service';
import { GhWinMarketParticipantsComponent } from './gh-win-market-participants.component';

@Component({
    selector: 'ms-gh-derived-win-market-participants',
    templateUrl: './gh-win-market-participants.component.html',
    standalone: false,
})
export class GhDerivedWinMarketParticipantsComponent extends GhWinMarketParticipantsComponent implements OnChanges {
    trapRunners: WinGameParticipant[];
    pickType: ParticipantPickType;
    isSubMarketChanged = false;

    constructor(
        sitecore: Sitecore,
        horsesFixtureSortingHelper: HorsesFixtureSortingHelper,
        horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        horsesFixturePricingHelper: HorsesFixturePricingHelper,
        racingService: RacingService,
        greyhoundTrackingService: GreyhoundTrackingService,
        horsesFixtureMarketHelper: HorsesFixtureMarketHelper,
        horsesRaceService: HorsesRaceService,
        baseRaceWinMarketService: BaseRaceWinMarketService,
        protected override participantsService: ParticipantsService,
    ) {
        super(
            sitecore,
            horsesFixtureSortingHelper,
            horsesFixtureParticipantHelper,
            horsesFixturePricingHelper,
            racingService,
            greyhoundTrackingService,
            horsesFixtureMarketHelper,
            horsesRaceService,
            baseRaceWinMarketService,
            participantsService,
        );
    }

    override ngOnChanges(changes: ISimpleChanges<GhWinMarketParticipantsComponent>): void {
        let isNewFixture = changes.fixture && changes.fixture.currentValue !== changes.fixture.previousValue;
        if (changes.fixture?.previousValue) {
            isNewFixture = changes.fixture.currentValue.id !== changes.fixture.previousValue.id;
        }
        const isSelectedMarketTitleChanged =
            changes.selectedMarketTitle && changes.selectedMarketTitle?.currentValue !== changes.selectedMarketTitle?.previousValue;
        this.isSubMarketChanged = !!(changes.optionGroup && changes.participantOptionGroups && !changes.fixture);
        if (isSelectedMarketTitleChanged) {
            this.isAllexpanded = false;
            this.vm.expandedParticipant = {};
            isNewFixture = true;
        }
        this.pickType = this.displayType === this.groupTypes.Wincast ? ParticipantPickType.Win : ParticipantPickType.Derived;
        this.setViewModel(isNewFixture);
    }

    override setViewModel(isNewFixture: boolean | undefined): void {
        this.isOptionBet = this.fixture.isOptionBet;
        let participants;
        if (isNewFixture || this.isSubMarketChanged) {
            const runners = this.horsesFixtureParticipantHelper.getParticipants(this.fixture.participants);
            this.vm.sortBy = this.isSubMarketChanged
                ? this.vm.sortBy
                : this.isAllPricesSP()
                  ? HorseRaceSortingDirection.CardNumber
                  : HorseRaceSortingDirection.Odds;
            participants = this.horsesFixtureSortingHelper.sortParticipantsByTrapNumberOfParticipant(runners, this.greyhoundTrapDogMapping);
            this.isAllexpanded = false;
            this.vm.expandedParticipant = {};
        } else {
            participants = this.restoreAndFilter(this.participants);
        }
        this.activeOption = this.baseRaceWinMarketService.getActiveSortOption(this.vm.sortBy);
        const options = this.horsesFixturePricingHelper.getOptionPrices(this.fixture, this.optionGroup, ParticipantPriceType.Fixed);
        this.participants = this.vm.runners = this.buildRunners(participants, options, this.fixture);
        if (isNewFixture) this.participantsSorting();
        else if (this.isSubMarketChanged) this.orderParticipantsBy(this.vm.sortBy, this.fixture, true);
        this.vm.nonRunners = this.horsesFixtureParticipantHelper.getCombinedNonRunners(this.fixture, true);
        this.isRunnerInfo =
            this.marketType === this.racingOptionMarketTypes.TrapWinner
                ? false
                : this.participants.some((participant) => this.setShowInfo(participant));

        if (this.dropDownItems.some((x) => x.id === HorseRaceSortingDirection.AtoZ)) {
            if (this.marketType === this.racingOptionMarketTypes.TrapWinner) {
                this.dropDownItems.pop();
            }
        } else {
            if (this.marketType !== this.racingOptionMarketTypes.TrapWinner) {
                this.dropDownItems.push({ id: HorseRaceSortingDirection.AtoZ, name: 'A-Z', type: OptionType.Regular });
            }
        }
    }

    buildRunners(
        participants: WinGameParticipant[],
        options: FixtureParticipantPriceMap | V3FixtureParticipantPriceMap,
        fixture: WinGameFixture,
        spOptions: FixtureParticipantPriceMap = {},
    ): (RacingParticipantWithEventOption | RacingParticipantWithParticipantOption)[] {
        return participants
            .map((p) => {
                let option: any;
                const spOption = spOptions[p.id];
                if (this.displayType === OptionGroupType.Wincast) {
                    const priceType = this.optionGroup.isWinPriceAvailable ? ParticipantPriceType.Fixed : ParticipantPriceType.StartingPrice;
                    option = this.horsesFixturePricingHelper.getParticipantOptions(p.id, this.participantOptionGroups, priceType);
                } else {
                    const partOption = this.participantOptionGroups[p.id];
                    if (partOption) option = partOption[0];
                }

                if (
                    this.marketType === this.racingOptionMarketTypes.BettingWithout &&
                    p.properties?.type !== ParticipantType.Favourite &&
                    p.properties?.type === ParticipantType.Trap
                ) {
                    const associatedFixtureParticipant = this.fixture.participants.find((q) => q.properties?.associatedFixtureParticipantId === p.id);
                    p.racingPostInfo = associatedFixtureParticipant?.racingPostInfo;
                }

                return {
                    ...p,
                    attributes: this.racingService.getRacingInfo(p),
                    option: option,
                    priceHistory: this.horsesFixturePricingHelper.getV3PriceHistory(option),
                    isOptionActive: this.horsesFixturePricingHelper.isV3WinOptionAvailable(option),
                    availableBadges: [],
                    easyBetViewStats: {},
                    spOption,
                    isSPOptionActive:
                        this.horsesFixturePricingHelper.isFixedOptionParticipant(p) &&
                        !this.horsesFixturePricingHelper.isPriceLocked(fixture, p, spOption) &&
                        this.racingService.isOptionVisible(p, ParticipantMarketType.Win, ParticipantPriceType.StartingPrice),
                };
            })
            .filter((t) => t.option);
    }

    override orderParticipantsBy(sortBy: HorseRaceSortingDirection, fixture: WinGameFixture, isSports: boolean): void {
        this.vm.sortBy = sortBy;
        const options = this.horsesFixturePricingHelper.getOptionPrices(this.fixture, this.optionGroup, ParticipantPriceType.Fixed);
        let sortedRunners;
        if (sortBy === HorseRaceSortingDirection.Odds) {
            sortedRunners = this.horsesFixtureSortingHelper.sortParticipantsByWinMarket(this.vm.runners, options);
        } else if (sortBy === HorseRaceSortingDirection.CardNumber) {
            sortedRunners = this.horsesFixtureSortingHelper.sortParticipantsByTrapNumberOfParticipant(this.vm.runners, this.greyhoundTrapDogMapping);
        } else {
            sortedRunners = this.horsesFixtureSortingHelper.sortParticipantsAlphabetically(this.vm.runners, this.greyhoundTrapDogMapping);
        }

        const participants = this.horsesFixtureParticipantHelper.getParticipants(sortedRunners);
        this.participants = this.buildRunners(participants, options, fixture) as RacingParticipantWithEventOption[];
    }

    override orderBy(sortBy: HorseRaceSortingDirection): void {
        const allPricesSP = this.isAllPricesSP();
        if (!allPricesSP || sortBy === HorseRaceSortingDirection.CardNumber || sortBy === HorseRaceSortingDirection.AtoZ) {
            this.activeOption.id = sortBy;
            this.orderParticipantsBy(sortBy, this.fixture, true);
        }
        this.greyhoundTrackingService.trackSorting(this.fixture, sortBy);
    }

    private isAllPricesSP(): boolean {
        const options = this.participants.map((x) => x.option as EventOption);
        let allPricesSP = false;
        if (this.fixture.isOptionBet) {
            allPricesSP = options.every((x) => x.participantPriceType === ParticipantPriceType.StartingPrice);
        }
        return allPricesSP;
    }

    participantsSorting() {
        const allPricesSP = this.isAllPricesSP();
        if (!allPricesSP) {
            this.orderParticipantsBy(HorseRaceSortingDirection.Odds, this.fixture, true);
            this.activeOption = this.baseRaceWinMarketService.getActiveSortOption(HorseRaceSortingDirection.Odds);
        }
    }
}
